import { BookingProposalType } from '@navio/nsdk';
import type { SeverityLevel } from '@sentry/react';

// App-specific event catalog. The reusable dispatcher, shipper interface, and
// base catalog (`userSignedUp`, `userLoggedIn`) live in `@navio/observability`.
// Here we augment that catalog via declaration merging so domain events become
// type-checked at every `tracking.track(...)` call site. Any free-form string
// still compiles via the loose `track(name, props?)` overload, so existing call
// sites are untouched. To migrate an event to type-checked, add it here.

/** Scrubbed failure payload shared by funnel `*Failed` events. */
export type TrackingFailureProps = {
  stage?: string;
  status?: number;
  errorCode?: string;
  errorMessage?: string;
};

export type FleetScheduleStep =
  | 'organization'
  | 'department'
  | 'address'
  | 'service'
  | 'salesItems'
  | 'cadence'
  | 'review'
  | 'availability'
  | 'cars';

export type FleetBookingStep = 'schedule' | 'vehicles' | 'service' | 'addons' | 'slots' | 'review';

type PartnerBookingStep =
  | 'customer'
  | 'member'
  | 'brand'
  | 'locationType'
  | 'address'
  | 'station'
  | 'cars'
  | 'products'
  | 'tireSeason'
  | 'tirePickup'
  | 'timeWindow'
  | 'insurance'
  | 'summary';

type TireOfferStep = 'customers' | 'tires' | 'review';

type AdminServiceOrgStep = 'organization' | 'department' | 'products';

/**
 * Screens of the customer booking-proposal flow, named rather than derived from
 * the URL. Every proposal lives on its own slug, so a path is unique per
 * recipient and cannot carry a funnel. Mobile and stationary deliberately share
 * `timePicker` and `summary` so one funnel spans both channels; `locationType`
 * on every event splits them again when that is what you want.
 */
export type ProposalStep = 'landing' | 'address' | 'editAddress' | 'timePicker' | 'login' | 'summary' | 'building';

/** How the customer landed on a time. */
type ProposalTimeSelectionMethod = 'suggested' | 'picker';

/**
 * Dimensions carried by every proposal event, so funnels can be broken down by
 * brand, service department and customer type without a join. Sent explicitly on
 * each event rather than relied on as super-props — a mid-flow logout wipes
 * session props, and a funnel must not lose its breakdown when that happens.
 */
export type ProposalAnalyticsProps = {
  proposalId: number;
  proposalType: BookingProposalType;
  isRecall: boolean;
  locationType: 'mobile' | 'stationary';
  customerType: 'organization' | 'personal';
  brand?: string;
  serviceDepartmentId: number;
  serviceDepartmentName?: string;
  /** Brand opens on three pre-picked times instead of the full calendar. */
  usesSuggestedTimes: boolean;
};

declare module '@navio/observability/events' {
  interface EventCatalog {
    'booking created': {
      bookingId?: number;
      isProposal?: boolean;
      brand?: string;
      paymentOption?: string;
      // Scalar department identity (analytics props must be primitives, never the
      // raw ServiceDepartment record). `name` is only set by flows whose response
      // exposes it — the core create response (ServiceDepartmentRecordList) has id only.
      executingServiceDepartmentId?: number;
      executingServiceDepartmentName?: string;
      locationType?: 'mobile' | 'stationary';
      amountGross?: number;
      amountPaid?: number;
      currency?: string;
    };
    bookingPaid: { bookingId?: number; amountPaid: number; currency?: string };
    'booking updated': { bookingId?: number };
    // Booking funnel step events — one per flow step so drop-off between steps
    // is measurable (Mixpanel funnel) and a failed create is distinguishable
    // from an abandoned one (`bookingFailed`).
    bookingFlowStarted: {
      brand?: string;
      locationType?: 'mobile' | 'stationary';
      garageId?: number;
      garageName?: string;
      serviceDepartmentId?: number;
    };
    // OpenPanel-only session search tags (plates are PII; see bookingFunnel.ts).
    bookingFlowContext: {
      locationType: 'mobile' | 'stationary';
      licensePlates?: string;
      serviceDepartmentName?: string;
    };
    addressStepCompleted: { brand?: string };
    carStepCompleted: { numberOfCars: number };
    salesItemsSelected: { numberOfCars: number };
    timeWindowStepCompleted: { startsAt?: string };
    summaryReached: { paymentOption?: string };
    bookingFailed: TrackingFailureProps & { stage: string };
    // Customer tire offer (quote) accept funnel. Partner generation is tireOffer*.
    tireOfferViewed: { slug?: string; brand?: string };
    tireOfferAccepted: { slug?: string; quoteItemId?: number; paymentOption?: string; brand?: string };
    tireOfferAcceptFailed: TrackingFailureProps;
    tireOfferExplanationOpened: { slug?: string; sheet: 'why' | 'wear' };
    tireOfferReminderSet: { slug?: string; option: string };
    tireOfferDeclined: { slug?: string; reasons: string[]; hasComment: boolean };
    // Auth funnel — pairs with the success-only userLoggedIn/userSignedUp so SMS
    // delivery friction (re-sends, retries) and verification failures are visible.
    loginSmsRequested: { isResend?: boolean };
    loginSmsCodeSubmitted: { attempt: number };
    loginFailed: { reason?: string };
    // --- Customer booking-proposal funnel (mobile + stationary, one vocabulary) ---
    // Every event ships to Mixpanel AND OpenPanel so the same funnel can be built
    // in either tool. Replaces the OpenPanel-only, recall-only `recallProposal*`
    // family; `isRecall` / `proposalType` keep recall filterable.
    /** Flow entered, once per proposal per analytics session. Funnel step 1. */
    proposalStarted: ProposalAnalyticsProps;
    /** One per screen of the flow. Funnel steps 2..n, slug-free. */
    proposalStepViewed: ProposalAnalyticsProps & { step: ProposalStep };
    /** Suggested times rendered — the denominator for suggested vs picker. */
    proposalSuggestedTimesViewed: ProposalAnalyticsProps & { numSuggestions: number };
    /** Customer left the pre-picked times (or the landing card) for the calendar. */
    proposalTimePickerOpened: ProposalAnalyticsProps & { from: 'landing' | 'suggestedTimes' };
    proposalTimeSelected: ProposalAnalyticsProps & { method: ProposalTimeSelectionMethod };
    proposalAccepted: ProposalAnalyticsProps & { bookingId?: number };
    proposalRejected: ProposalAnalyticsProps;

    // --- Backoffice product funnels (partner + admin internal tooling) ---
    // These ship to OpenPanel ONLY (via `tracking.trackOpenPanel`) — Mixpanel is
    // on a limited free-tier quota, so higher-volume internal-tool events stay
    // off it. Naming: *Started / *StepCompleted / *Created|*Succeeded / *Failed.

    // Partner: fleet schedule create wizard (API create mid-flow + wizard complete)
    fleetScheduleStarted: { serviceOrganizationId?: number; numDepartments?: number };
    fleetScheduleStepCompleted: { step: FleetScheduleStep };
    fleetScheduleCreated: {
      scheduleId: number;
      serviceCategoryId?: number;
      intervalDays?: number;
      numSalesItems?: number;
    };
    fleetScheduleWizardCompleted: { scheduleId: number };
    fleetScheduleFailed: TrackingFailureProps;

    // Partner: fleet booking create flow
    fleetBookingStarted: { numServiceDepartments?: number };
    fleetBookingStepCompleted: { step: FleetBookingStep };
    fleetBookingCreated: { scheduleId?: number; numVehicles: number; numSlots: number };
    fleetBookingFailed: TrackingFailureProps;

    // Partner: customer / user-group booking flow (partner books for a customer)
    partnerBookingStarted: { brand?: string };
    partnerBookingStepCompleted: { step: PartnerBookingStep };
    partnerBookingCreated: { bookingId?: number; numCars?: number; locationType?: string };
    partnerBookingFailed: TrackingFailureProps & { stage: string };

    // Partner: tire offer / lead flows
    tireOfferStarted: { fromLead: boolean; leadGenerationId?: number };
    tireOfferStepCompleted: { step: TireOfferStep };
    tireOfferCreated: { numCars: number; wheelSetType?: string; fromLead: boolean; sendSms: boolean };
    tireOfferFailed: TrackingFailureProps;

    // Command palette / spotlight search (OpenPanel-only, high volume)
    commandPaletteSearchSelected: {
      source?: 'page' | 'spotlight' | 'recent' | 'action' | 'department';
      model?: string;
      queryLength?: number;
    };

    // Partner: worker onboarding (separate pages, action events — not a funnel)
    workerCreated: { workerType?: string; fromExistingUser: boolean };
    workerContractCreated: { serviceWorkerId?: number };
    workerVehicleAssigned: { serviceWorkerId?: number; numCapabilities?: number };

    // Admin: create service organization (partner onboarding funnel)
    adminServiceOrgStarted: Record<string, never>;
    adminServiceOrgStepCompleted: { step: AdminServiceOrgStep };
    adminServiceOrgCreated: { serviceOrganizationId: number; serviceDepartmentId?: number };
    adminServiceOrgFailed: TrackingFailureProps & { stage: AdminServiceOrgStep };
    // Marker on the session-replay timeline (OpenPanel, Mixpanel when recording)
    // for every error Sentry accepted, so a replay can be scrubbed to the report.
    sentryError: { sentryEventId: string; level?: SeverityLevel; errorType?: string; errorMessage?: string };
  }
}

export const commonTrackingEvents = {
  sentryError: 'sentryError',
  bookingCreated: 'booking created',
  bookingPaid: 'bookingPaid',
  unavailableAddressArea: 'unavailable address area',
  userSignedUp: 'userSignedUp',
  bookingUpdated: 'booking updated',
  userLoggedIn: 'userLoggedIn',
  salesItemDescriptionDetailsClicked: 'salesItemDescriptionDetailsClicked',
  bookingFlowStarted: 'bookingFlowStarted',
  bookingFlowContext: 'bookingFlowContext',
  addressStepCompleted: 'addressStepCompleted',
  carStepCompleted: 'carStepCompleted',
  salesItemsSelected: 'salesItemsSelected',
  timeWindowStepCompleted: 'timeWindowStepCompleted',
  summaryReached: 'summaryReached',
  bookingFailed: 'bookingFailed',
  tireOfferViewed: 'tireOfferViewed',
  tireOfferAccepted: 'tireOfferAccepted',
  tireOfferExplanationOpened: 'tireOfferExplanationOpened',
  tireOfferReminderSet: 'tireOfferReminderSet',
  tireOfferDeclined: 'tireOfferDeclined',
  tireOfferAcceptFailed: 'tireOfferAcceptFailed',
  loginSmsRequested: 'loginSmsRequested',
  loginSmsCodeSubmitted: 'loginSmsCodeSubmitted',
  loginFailed: 'loginFailed',
  proposalStarted: 'proposalStarted',
  proposalStepViewed: 'proposalStepViewed',
  proposalSuggestedTimesViewed: 'proposalSuggestedTimesViewed',
  proposalTimePickerOpened: 'proposalTimePickerOpened',
  proposalTimeSelected: 'proposalTimeSelected',
  proposalAccepted: 'proposalAccepted',
  proposalRejected: 'proposalRejected',
  // Backoffice product funnels — OpenPanel only (see `tracking.trackOpenPanel`).
  fleetScheduleStarted: 'fleetScheduleStarted',
  fleetScheduleStepCompleted: 'fleetScheduleStepCompleted',
  fleetScheduleCreated: 'fleetScheduleCreated',
  fleetScheduleWizardCompleted: 'fleetScheduleWizardCompleted',
  fleetScheduleFailed: 'fleetScheduleFailed',
  fleetBookingStarted: 'fleetBookingStarted',
  fleetBookingStepCompleted: 'fleetBookingStepCompleted',
  fleetBookingCreated: 'fleetBookingCreated',
  fleetBookingFailed: 'fleetBookingFailed',
  partnerBookingStarted: 'partnerBookingStarted',
  partnerBookingStepCompleted: 'partnerBookingStepCompleted',
  partnerBookingCreated: 'partnerBookingCreated',
  partnerBookingFailed: 'partnerBookingFailed',
  tireOfferStarted: 'tireOfferStarted',
  tireOfferStepCompleted: 'tireOfferStepCompleted',
  tireOfferCreated: 'tireOfferCreated',
  tireOfferFailed: 'tireOfferFailed',
  workerCreated: 'workerCreated',
  workerContractCreated: 'workerContractCreated',
  workerVehicleAssigned: 'workerVehicleAssigned',
  adminServiceOrgStarted: 'adminServiceOrgStarted',
  adminServiceOrgStepCompleted: 'adminServiceOrgStepCompleted',
  adminServiceOrgCreated: 'adminServiceOrgCreated',
  adminServiceOrgFailed: 'adminServiceOrgFailed'
} as const;
